import { Controller } from 'stimulus'

/**
 * Used for "multistep" lead form (/apply/lead)
 *
 * @class LeadFormController
 * @memberof Controllers
 *
 */
export default class extends Controller {
  static currentStep
  static targets = [
    'step',
    'firstStep',
    'secondStep',
    'thirdStep',
    'thankYouStep',
    'endorser',
    'originalEndorser',
    'source',
    'originalSource',
    'internetSource',
    'dmLead',
    'stateSelect',
    'firstName',
    'lastName',
    'email',
    'phone',
    'noRedirect',
    'submitButton',
    'savePreferencesButton',
    'deferDispatch',
    'preferredContactMethod',
    'bestTimeToContact',
    'referralSource',
    'influencerName',
    'borrowerTimezoneOffset',
    'initiative'
  ]

  /**
   * @function Controllers.LeadFormController.connect
   * @memberof Controllers
   * @description On connection show first step of the flow
   */
  connect() {
    this._showStep(this.firstStepTarget)
    this._sendGTMDataLayer('firstStep')
    this._initRecaptcha()
    this._captureBorrowerTimezoneOffset()
  }

  /**
   * @function Controllers.LeadFormController.next
   * @memberof Controllers
   * @description Go to the second step in the form
   * @param {Event} event
   */
  next(event) {
    if (event) { event.preventDefault() }

    if (this._stepOneValid) {
      this._showStep(this.secondStepTarget)
      this._sendGTMDataLayer('secondStep')
      document.getElementById('lead_first_name').focus()
    }
  }

  /**
   * @function Controllers.LeadFormController.submit
   * @memberof Controllers
   * @description Submit the lead, show the Thank You step
   * @param {Event} event
   */
  async submit(event) {
    if (event) { event.preventDefault() }

    await this._submitLead(this.thankYouStepTarget)
  }

  /**
   * @function Controllers.LeadFormController.submitAndContinue
   * @memberof Controllers
   * @description Submit the lead, then continue to the contact-preferences step
   * @param {Event} event
   */
  async submitAndContinue(event) {
    if (event) { event.preventDefault() }

    await this._submitLead(this.thirdStepTarget)
  }

  /**
   * @function Controllers.LeadFormController.savePreferences
   * @memberof Controllers
   * @description Sends the borrower's contact preferences, then shows the Thank You step
   * @param {Event} event
   */
  async savePreferences(event) {
    if (event) { event.preventDefault() }

    // The countdown may have already submitted this step and moved on.
    if (this._preferencesSent) { return }

    this._clearPreferencesTimers()
    this._loadBouncer(this.savePreferencesButtonTarget)
    await this._sendPreferences()
    this._showThankYou('saved')
  }

  /**
   * @function Controllers.LeadFormController.resetInactivityTimer
   * @memberof Controllers
   * @description Restarts the auto-submit countdown whenever the borrower interacts with
   *   the preferences step. Does nothing once the countdown has already been spent.
   *
   *   Deliberately leaves the deadline timer alone. That one is the ceiling on this step, and a
   *   ceiling that interaction could push back would be no ceiling at all.
   */
  resetInactivityTimer() {
    if (this._inactivityTimer) { this._startInactivityTimer() }
  }

  /**
   * @function Controllers.LeadFormController.syncPreferenceLabel
   * @memberof Controllers
   * @description Clears the unselected state off a preference select once it holds a value,
   *   which shrinks its label to the top of the field to make room. CSS cannot see a select's
   *   selected option change, so the class carries that state.
   * @param {Event} event
   */
  syncPreferenceLabel(event) {
    const select = event.target
    if (!select.classList.contains('form__select--labeled')) { return }

    select.classList.toggle('form__select--unselected', select.value === '')
  }

  /**
   * @function Controllers.LeadFormController.teardown
   * @memberof Controllers
   * @description Called on turbolinks:before-cache so the countdowns do not outlive the page
   */
  teardown() {
    this._clearPreferencesTimers()
  }

  /**
   * @function Controllers.LeadFormController.navBack
   * @memberof Controllers
   * @description Shows previous step if possible
   * @param {Event} event
   */
  navBack(event) {
    event.preventDefault()

    // Deliberately no route back out of the preferences step: the lead was already submitted
    // on step two, so returning there and submitting again would create a duplicate.
    if (this.currentStep === this.secondStepTarget) {
      this._showStep(this.firstStepTarget)
    }
  }

  /**
   * @function Controllers.LeadFormController._stepOneValid
   * @memberof Controllers
   * @description Validates if the first step is valid
   * @private
   * @return {boolean}
   */
  get _stepOneValid() {
    const fields = [
      document.getElementById('lead_state'),
      document.getElementById('lead_type_purchase'),
      document.getElementById('lead_type_refinance')
    ]

    return this._validationOf(fields)
  }

  /**
   * @function Controllers.LeadFormController._stepTwoValid
   * @memberof Controllers
   * @description Validates if the second step is valid
   * @private
   * @return {boolean}
   */
  get _stepTwoValid() {
    const fields = [
      document.getElementById('lead_first_name'),
      document.getElementById('lead_last_name'),
      document.getElementById('lead_email'),
      document.getElementById('lead_phone')
    ]

    return this._validationOf(fields)
  }

  /**
   * @function Controllers.LeadFormController._validationOf
   * @memberof Controllers
   * @description Validates all provided input fields
   * @param {Array} fields to validate
   * @private
   * @return {boolean}
   */
  _validationOf(fields) {
    const validity = fields.map(field => field.checkValidity())

    this.element.reportValidity()

    return validity.every(field => field === true)
  }

  /**
   * @function Controllers.LeadFormController._submitLead
   * @memberof Controllers
   * @description Validates and posts the lead, then advances to the given step. Direct mail
   *   leads are redirected off to Floify instead and never reach a later step.
   * @param {target} nextStep to show once the lead is accepted
   * @private
   */
  async _submitLead(nextStep) {
    if (!this._stepTwoValid) { return }

    this._loadBouncer(this.submitButtonTarget)

    const isDmLead = this.dmLeadTarget.value
    const result = await this._submitForm()

    if (!result) {
      this._clearBouncer(this.submitButtonTarget)
      return
    }

    if (isDmLead && result.redirect_url && result.redirect_url.includes('floify.com/verify')) {
      window.location.href = result.redirect_url
      return
    }

    this._preferencesToken = result.preferences_token
    this._preferencesDeadlineSeconds = result.preferences_deadline_seconds

    // The has-check has to come first: Stimulus target getters throw when the element is absent
    // rather than returning undefined, and === evaluates both sides. With the preferences step
    // turned off in the admin there is no third step, and reading it here would throw between
    // creating the lead and showing the borrower the thank-you step.
    if (this.hasThirdStepTarget && nextStep === this.thirdStepTarget) {
      this._showStep(this.thirdStepTarget)
      this._sendGTMDataLayer('thirdStep')
      this._startPreferencesTimers()
    } else {
      this._showThankYou()
    }
  }

  /**
   * @function Controllers.LeadFormController._fetchSubmitForm
   * @memberof Controllers
   * @description Submits the form via fetch API
   * @return {Promise<Object|null>} parsed response, or null if the lead was rejected
   * @private
   */
  async _submitForm() {
    const lead = this._formValues

    const recaptchaToken = await this._getRecaptchaToken()
    if (recaptchaToken) {
      lead.recaptcha_token = recaptchaToken
    }

    const body = { lead, no_redirect: this.noRedirectTarget.value }
    if (this.hasDeferDispatchTarget) {
      body.defer_dispatch = this.deferDispatchTarget.value
    }

    try {
      const response = await fetch('/leads', {
        method:      'POST',
        credentials: 'same-origin',
        headers:     { 'Content-Type': 'application/json' },
        mode:        'cors',
        body:        JSON.stringify(body)
      })

      if (!response.ok) { return null }

      return await response.json()
    } catch (e) {
      return null
    }
  }

  /**
   * @function Controllers.LeadFormController._sendPreferences
   * @memberof Controllers
   * @description Attaches the borrower's contact preferences to the already-created lead.
   *   Failures are swallowed on purpose — the lead itself is already safe, and the server
   *   dispatches it regardless, so nothing here should keep the borrower off the Thank You step.
   * @private
   */
  async _sendPreferences() {
    if (!this._preferencesToken || this._preferencesSent) { return }

    this._preferencesSent = true

    try {
      await fetch('/lead_preferences', {
        method:      'PATCH',
        credentials: 'same-origin',
        headers:     { 'Content-Type': 'application/json' },
        mode:        'cors',
        body:        JSON.stringify({ token: this._preferencesToken, lead: this._preferenceValues })
      })
    } catch (e) {
      // Intentionally ignored; see above.
    }
  }

  /**
   * @function Controllers.LeadFormController._startPreferencesTimers
   * @memberof Controllers
   * @description Starts the two countdowns that govern step three: the inactivity timer, which
   *   any interaction restarts, and the deadline, which nothing restarts.
   * @private
   */
  _startPreferencesTimers() {
    this._startInactivityTimer()
    this._startDeadlineTimer()
  }

  /**
   * @function Controllers.LeadFormController._clearPreferencesTimers
   * @memberof Controllers
   * @description Cancels both step-three countdowns
   * @private
   */
  _clearPreferencesTimers() {
    this._clearInactivityTimer()
    this._clearDeadlineTimer()
  }

  /**
   * @function Controllers.LeadFormController._startDeadlineTimer
   * @memberof Controllers
   * @description Starts the ceiling on how long the borrower can stay on step three. The
   *   inactivity countdown restarts on every interaction, so without this an engaged borrower
   *   could outlast the server's dispatch and have their preferences arrive after the lead was
   *   already sent — at which point they can never be delivered. The server sizes this to expire
   *   before that dispatch and sends it back with the created lead.
   *
   *   Absent from the response (preferences disabled, or a lead that dispatched immediately),
   *   there is no dispatch to beat and no deadline to enforce.
   * @private
   */
  _startDeadlineTimer() {
    this._clearDeadlineTimer()

    const seconds = parseInt(this._preferencesDeadlineSeconds, 10)
    if (!(seconds > 0)) { return }

    this._deadlineTimer = setTimeout(() => this._autoSubmitPreferences('deadline_reached'), seconds * 1000)
  }

  /**
   * @function Controllers.LeadFormController._clearDeadlineTimer
   * @memberof Controllers
   * @description Cancels any pending step-three deadline
   * @private
   */
  _clearDeadlineTimer() {
    if (this._deadlineTimer) {
      clearTimeout(this._deadlineTimer)
      this._deadlineTimer = null
    }
  }

  /**
   * @function Controllers.LeadFormController._startInactivityTimer
   * @memberof Controllers
   * @description Starts (or restarts) the countdown that submits the preferences step for a
   *   borrower who walks away from it. Threshold comes from the admin-configurable
   *   data-auto-submit-seconds attribute.
   * @private
   */
  _startInactivityTimer() {
    this._clearInactivityTimer()

    const seconds = parseInt(this.element.dataset.autoSubmitSeconds, 10) || 30

    this._inactivityTimer = setTimeout(() => this._autoSubmitPreferences(), seconds * 1000)
  }

  /**
   * @function Controllers.LeadFormController._clearInactivityTimer
   * @memberof Controllers
   * @description Cancels any pending auto-submit countdown
   * @private
   */
  _clearInactivityTimer() {
    if (this._inactivityTimer) {
      clearTimeout(this._inactivityTimer)
      this._inactivityTimer = null
    }
  }

  /**
   * @function Controllers.LeadFormController._autoSubmitPreferences
   * @memberof Controllers
   * @description Submits whatever the borrower has entered, either because they went idle or
   *   because the step hit its deadline. Untouched fields are sent blank rather than defaulted.
   * @param {string} [outcome] which countdown ended the step, reported to GTM
   * @private
   */
  async _autoSubmitPreferences(outcome = 'auto_submitted') {
    this._clearPreferencesTimers()
    await this._sendPreferences()
    this._showThankYou(outcome)
  }

  /**
   * @function Controllers.LeadFormController._showThankYou
   * @memberof Controllers
   * @description Shows the Thank You step and reports completion to GTM
   * @param {string} [preferencesOutcome] how the preferences step ended, when there was one
   * @private
   */
  _showThankYou(preferencesOutcome) {
    this._showStep(this.thankYouStepTarget)
    this._sendGTMDataLayer('thankYouStep', preferencesOutcome ? { 'preferences_outcome': preferencesOutcome } : {})
  }

  /**
   * @function Controllers.LeadFormController._showStep
   * @memberof Controllers
   * @description Shows the passed in step
   * @param {target} step to show
   * @private
   */
  /**
   * @description Captures the borrower's timezone offset in seconds
   * @private
   */
  _captureBorrowerTimezoneOffset() {
    this.borrowerTimezoneOffsetTarget.value = new Date().getTimezoneOffset() * -60
  }

  /**
   * @description Loads the reCAPTCHA v3 script if a site key is present
   * @private
   */
  _initRecaptcha() {
    this.recaptchaSiteKey = this.element.dataset.recaptchaSiteKey
    if (!this.recaptchaSiteKey || window['grecaptcha']) { return }

    const tag = document.createElement('script')
    tag.id = 'recaptcha-js-api'
    tag.src = `https://www.google.com/recaptcha/api.js?render=${this.recaptchaSiteKey}`
    document.head.appendChild(tag)
  }

  /**
   * @description Executes reCAPTCHA and returns the token, or null on failure
   * @return {Promise<string|null>} reCAPTCHA token
   * @private
   */
  async _getRecaptchaToken() {
    if (!this.recaptchaSiteKey || !window['grecaptcha']) { return null }

    try {
      /* global Promise */
      return await new Promise(resolve => {
        window['grecaptcha'].ready(() => {
          window['grecaptcha'].execute(this.recaptchaSiteKey, { action: 'submit' }).then(resolve)
        })
      })
    } catch (e) {
      return null
    }
  }

  /**
   * @description Shows the passed in step
   * @param {target} step to show
   * @private
   */
  _showStep(step) {
    this._hideAllSteps()
    step.classList.add(this._activeStepCssClass)
    this.currentStep = step
    window.scrollTo({ top: 0 })

    // make sure we don't trap people in the lead form
    if (step !== this.firstStepTarget) {
      window.history.pushState({}, null, null)
    }
  }

  /**
   * @function Controllers.LeadFormController._hideAllSteps
   * @memberof Controllers
   * @description Hides all steps
   * @private
   */
  _hideAllSteps() {
    this.stepTargets.forEach(step => {
      step.classList.remove(this._activeStepCssClass)
    })
  }

  /**
   * @function Controllers.LeadFormController._sendGTMDataLayer
   * @memberof Controllers
   * @description Send data to GTM for reporting
   * @param {string} step description
   * @param {Object} [extraData] additional keys to merge into the push
   * @private
   */
  _sendGTMDataLayer(step, extraData) {
    window.dataLayer = window.dataLayer || []
    const applicationStart = (step === 'firstStep').toString()
    const applicationComplete = (step === 'thankYouStep').toString()

    const data = {
      'application_start':    applicationStart,
      'application_complete': applicationComplete,
      'current_page':         step,
      'session_uuid':         sessionStorage.getItem('sessionUUID'),
      'event':                'turboPageLoad',
      'product_type':         this._leadType,
      'form_type':            this.hasThirdStepTarget ? '3-step' : '2-step'
    }

    if (this.hasInitiativeTarget && this.initiativeTarget.value) {
      data['campaign'] = this.initiativeTarget.value
    }

    window.dataLayer.push(Object.assign(data, extraData || {}))
  }

  /**
   * @function Controllers.LeadFormController._loadBouncer
   * @memberof Controllers
   * @description Swaps a button's contents for the bouncing loader, remembering the original
   *   so a rejected submission can put the button back.
   * @param {HTMLElement} [button] defaults to the step two submit button
   * @private
   */
  _loadBouncer(button) {
    const target = button || this.submitButtonTarget

    if (target.dataset.buttonHtml === undefined) {
      target.dataset.buttonHtml = target.innerHTML
    }

    target.innerHTML = `
      <div class="bouncing-loader">
        <div></div>
        <div></div>
        <div></div>
      </div>
    `
  }

  /**
   * @function Controllers.LeadFormController._clearBouncer
   * @memberof Controllers
   * @description Restores a button swapped out by _loadBouncer
   * @param {HTMLElement} button
   * @private
   */
  _clearBouncer(button) {
    if (button.dataset.buttonHtml !== undefined) {
      button.innerHTML = button.dataset.buttonHtml
    }
  }

  /**
   * @function Controllers.LeadFormController._activeStepCssClass
   * @memberof Controllers
   * @description Gets the CSS Class Name used for showing steps
   * @private
   * @return {string}
   */
  get _activeStepCssClass() {
    return this.element.dataset.leadFormActiveClassValue
  }

  /**
   * @function Controllers.LeadFormController._isPurchaseLead
   * @memberof Controllers
   * @description Gets whether or not the current lead is a purchase
   * @private
   * @return {boolean}
   */
  get _isPurchaseLead() {
    return document.getElementById('lead_type_purchase').checked
  }

  /**
   * @function Controllers.LeadFormController._isRefinanceLead
   * @memberof Controllers
   * @description Gets whether or not the current lead is a refinance
   * @private
   * @return {boolean}
   */
  get _isRefinanceLead() {
    return document.getElementById('lead_type_refinance').checked
  }

  /**
   * @function Controllers.LeadFormController._leadType
   * @memberof Controllers
   * @description Gets current lead type, 'purchase', 'refinance' or 'tbd'
   * @private
   * @return {string}
   */
  get _leadType() {
    if (this._isPurchaseLead === true) {
      return 'purchase'
    } else if (this._isRefinanceLead === true) {
      return 'refinance'
    } else {
      return 'tbd'
    }
  }

  /**
   * @function Controllers.LeadFormController._formValues
   * @memberof Controllers
   * @description Gathers all form values into an object
   * @private
   * @return {Object}
   */
  get _formValues() {
    const dmLead = this.dmLeadTarget.value
    const endorser = this.endorserTarget.value
    const source = this.sourceTarget.value
    const internetSource = this.internetSourceTarget.value
    const originalEndorser = this.originalEndorserTarget.value
    const originalSource = this.originalSourceTarget.value
    const state = this.stateSelectTarget.value
    const firstName = this.firstNameTarget.value
    const lastName = this.lastNameTarget.value
    const email = this.emailTarget.value
    const phone = this.phoneTarget.value
    const type = this._leadType
    const borrowerTimezoneOffset = this.borrowerTimezoneOffsetTarget.value
    const initiative = this.hasInitiativeTarget ? this.initiativeTarget.value : null

    const values = {
      dm_lead:                  dmLead,
      endorser,
      source,
      internet_source:          internetSource,
      original_endorser:        originalEndorser,
      original_source:          originalSource,
      state,
      first_name:               firstName,
      last_name:                lastName,
      email,
      phone,
      type,
      borrower_timezone_offset: borrowerTimezoneOffset
    }

    if (initiative) { values.initiative = initiative }

    return values
  }

  /**
   * @function Controllers.LeadFormController._preferenceValues
   * @memberof Controllers
   * @description Gathers the contact-preference step's values. Untouched fields come back as
   *   empty strings so an abandoned step is never recorded as a deliberate choice.
   * @private
   * @return {Object}
   */
  get _preferenceValues() {
    return {
      preferred_contact_method: this.hasPreferredContactMethodTarget ? this.preferredContactMethodTarget.value : '',
      best_time_to_contact:     this.hasBestTimeToContactTarget ? this.bestTimeToContactTarget.value : '',
      referral_source:          this.hasReferralSourceTarget ? this.referralSourceTarget.value : '',
      influencer_name:          this.hasInfluencerNameTarget ? this.influencerNameTarget.value : ''
    }
  }
}
